import * as React from 'react';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../components/Button';
import DateTimeInput from '../../components/DateTimeInput';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import SingleSelect from '../../components/SingleSelect';
import GlobalModal from '../../components/GlobalModal';
import AddWitnessModal from './Modals/AddWitnessModal';
import DisplayPhoneNumber from '../../components/DisplayPhoneNumber';
import DateInput from '../../components/DateInput';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';
import { ReactComponent as NoteIcon } from '../../assets/svg/note-icon.svg';

// TYPES
import { IncidentFormTypes } from './IncidentSubmit';
import { GlobalLookupsuid } from '../../api/models';

interface StepOneTypes {
	incidentFormData: IncidentFormTypes;
	setIncidentFormData: React.Dispatch<React.SetStateAction<IncidentFormTypes>>;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;
}

const StepOne: React.FC<StepOneTypes> = ({ incidentFormData, setIncidentFormData, setActiveStep }) => {
	const [specificationTypes, setSpecificationTypes] = React.useState<GlobalLookupsuid[]>([]);

	// Get all incident Types from Redux store
	const incidentTypes = useAppSelector((state) => state.lists.incidentTypes);
	const incidentSpecifications = useAppSelector((state) => state.lists.incidentSpecifications) || [];

	// State to manage Modal
	const [showAddWitnessModal, setShowAddWitnessModal] = React.useState<boolean>(false);

	// Creat all age Options for age field
	const ageOptions: { label: string; value: string }[] = [];

	[...Array(18)].map((item, i) => {
		ageOptions.push({ value: (i + 1).toLocaleString(), label: (i + 1).toLocaleString() });
	});

	// Submit function
	const onSubmitStepOne = (values: any) => {
		setActiveStep('step2');

		window.scrollTo(0, 0);

		setIncidentFormData((prevState) => ({
			...prevState,

			name: values.name,
			age: values.age,
			dateOfBirth: values.dateOfBirth,
			title: values.title,
			occurrence: values.occurrence,
			type: values.type,
			specification: values.specification,
			location: values.location,
			description: values.description,
			witness: values.witness,
			witnesses: values.witnesses
		}));
	};

	const validationSchemaStepOne = Yup.object({
		name: Yup.string().required('Required'),
		age: Yup.string().required('Required'),
		title: Yup.string().required('Required'),
		occurrence: Yup.string().required('Required'),
		type: Yup.string().required('Required'),
		specification: Yup.string().required('Required'),
		location: Yup.string().required('Required'),
		description: Yup.string().required('Required'),
		witnesses: Yup.array().when('witness', {
			is: true,
			then: Yup.array().min(1, 'Required')
		})
	});

	const initialValuesStepOne = {
		name: incidentFormData.name || '',
		age: incidentFormData.age || '',
		dateOfBirth: incidentFormData.dateOfBirth || '',
		title: incidentFormData.title || '',
		occurrence: incidentFormData.occurrence || '',
		type: incidentFormData.type || '',
		specification: incidentFormData.specification || '',
		location: incidentFormData.location || '',
		description: incidentFormData.description || '',
		witness: incidentFormData.witness || false,
		witnesses: incidentFormData.witnesses || []
	};

	return (
		<Formik
			initialValues={initialValuesStepOne}
			validationSchema={validationSchemaStepOne}
			onSubmit={onSubmitStepOne}
			enableReinitialize
			validateOnBlur
		>
			{(formik) => {
				React.useEffect(() => {
					const typeVal = formik.values.type;

					const specificationFilter = incidentSpecifications.find((type) => type.value === typeVal)?.items || [];

					setSpecificationTypes(specificationFilter);
				}, [formik.values.type]);

				return (
					<Form translate="yes" className="general-form">
						<h3>Electronic signature acknowledgement</h3>

						<div className="general-divider"></div>

						<div className="general-notification-box">
							<NoteIcon />

							<div className="info-text w-100">
								<p>
									<strong>
										Please note that at the end of this form you will be asked to sign the form with an Electronic
										Signature.
									</strong>
								</p>

								<p>
									By submitting this form you consent to the use of an Electronic Signature as the legal equivalent of
									your handwritten signature.
								</p>
							</div>
						</div>

						<div className="general-form-layout-wrapper">
							<div className="form-main w-100">
								<h3>Child's details</h3>

								<div className="general-divider mbtom-30"></div>

								<div className="row row-w-60">
									<div className="form-group col-lg-6 mbtom-0">
										<label htmlFor="name" className="label">
											Full Name
										</label>

										<div className="form-field">
											<Field name="name" id="name" type="text" className="form-input" />

											<ErrorMessage name="name" component="div" className="error-message" />
										</div>
									</div>

									<div className="form-group col-lg-6 gap-right-0 mbtom-0">
										<label htmlFor="age" className="label">
											Age
										</label>

										<div className="form-field">
											<SingleSelect
												options={ageOptions}
												name="age"
												value={formik.values.age}
												onChange={(val: string) => formik.setFieldValue('age', val)}
												placeholder=""
											/>

											<ErrorMessage name="age" component="div" className="error-message" />
										</div>
									</div>
								</div>

								<div className="form-group w-60">
									<label className="label">Date of Birth</label>

									<DateInput
										dateTimeString={formik.values.dateOfBirth || ''}
										onChange={(val: string) => formik.setFieldValue('dateOfBirth', val)}
										showDropdowns={true}
										excludeFuture={true}
									/>
								</div>

								<h3>Details of incident, injury, trauma or illness</h3>

								<div className="general-divider"></div>

								<div className="form-group w-60">
									<label htmlFor="title" className="label">
										Incident title
									</label>

									<div className="form-field">
										<Field
											name="title"
											id="title"
											placeholder={'E.g. Fall in playground'}
											type="text"
											className="form-input"
										/>

										<ErrorMessage name="title" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group w-60">
									<label className="label">Date and time of incident</label>

									<DateTimeInput
										dateTimeString={formik.values.occurrence}
										onChange={(val: string) => formik.setFieldValue('occurrence', val)}
										excludeFuture
									/>
								</div>

								<div className="form-group w-60">
									<label htmlFor="type" className="label">
										Incident type
									</label>

									<SingleSelect
										options={incidentTypes}
										name="type"
										value={formik.values.type}
										placeholder="Select a type"
										onChange={(val: string) => {
											formik.setFieldValue('type', val);

											const specificationFilter =
												incidentSpecifications.find((type) => type.value === val)?.items || [];

											setSpecificationTypes(specificationFilter);
										}}
									/>

									<ErrorMessage name="type" component="div" className="error-message" />
								</div>

								{formik.values.type ? (
									<div className="form-group w-60">
										<label htmlFor="specification" className="label">
											Category
										</label>

										<SingleSelect
											options={specificationTypes}
											name="specification"
											value={formik.values.specification}
											placeholder="Select a category"
											onChange={(val: string) => formik.setFieldValue('specification', val)}
										/>

										<ErrorMessage name="specification" component="div" className="error-message" />
									</div>
								) : null}

								<div className="form-group w-60">
									<label htmlFor="location" className="label">
										Location of the incident
									</label>

									<p className="sub-grey-text small">
										Please include the address and a description of where on the premises the incident, injury, trauma
										or illness occurred.
									</p>

									<div className="form-field">
										<Field as="textarea" name="location" id="location" type="text" className="form-input" />

										<ErrorMessage name="location" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group w-60">
									<label htmlFor="description" className="label">
										Description of the incident
									</label>

									<p className="sub-grey-text small">
										Please describe what occurred. What were the surrounding circumstances that led to the incident,
										injury, trauma or illness, including any symptoms
									</p>

									<div className="form-field">
										<Field as="textarea" name="description" id="description" type="text" className="form-input" />

										<ErrorMessage name="description" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group w-60">
									<label className="label">Was there any witness?</label>

									<p className="sub-grey-text small">
										Please record the full name, contact details and role of all witnesses
									</p>

									<div className="form-field radio-group">
										<div className="radio-row">
											<Field
												name="witness"
												id="witness"
												type="radio"
												value={true}
												checked={formik.values.witness === true}
												onChange={() => formik.setFieldValue('witness', true)}
											/>

											<span className="radio-option">Yes</span>
										</div>

										<div className="radio-row">
											<Field
												name="witness"
												id="witness"
												type="radio"
												value={false}
												checked={formik.values.witness === false}
												onChange={() => formik.setFieldValue('witness', false)}
											/>

											<span className="radio-option">No</span>
										</div>

										<ErrorMessage name="witness" component="div" className="error-message" />
									</div>
								</div>

								{formik.values.witness === true && (
									<div className="form-group w-60">
										<label>List of witness</label>

										<div className="general-divider mbtom-0"></div>

										<FieldArray name="witnesses">
											{(props) => {
												const { push, remove, form } = props;
												const { values } = form;
												const { witnesses } = values;

												return (
													<>
														{witnesses?.length > 0 &&
															witnesses.map((item: any, index: number) => {
																return (
																	<div className="incident-detail-row row" key={index}>
																		<div className="col-4 pdl-0">
																			<p className="name">{item.name}</p>
																			<p>{item.role}</p>
																		</div>

																		<div className="col-5">
																			<p>{item.email}</p>
																			<p>
																				<DisplayPhoneNumber>{item.phone}</DisplayPhoneNumber>
																			</p>
																		</div>

																		<div className="col-3 text-right pdr-0">
																			<Button
																				onClick={() => {
																					remove(index);
																				}}
																				className="btn-text"
																			>
																				Remove
																			</Button>
																		</div>
																	</div>
																);
															})}

														<Button
															onClick={() => {
																setIncidentFormData((prevState) => ({
																	...prevState,

																	name: values.name,
																	age: values.age,
																	title: values.title,
																	occurrence: values.occurrence,
																	type: values.type,
																	specification: values.specification,
																	location: values.location,
																	description: values.description,
																	witness: values.witness,
																	witnesses: values.witnesses
																}));

																setShowAddWitnessModal(true);
															}}
															type="button"
															className="add-witness"
														>
															<PlusIcon className="icon" />
															Add a witness
														</Button>
													</>
												);
											}}
										</FieldArray>

										<ErrorMessage name="witnesses">
											{(error) => <div className="form-validation-error">{error}</div>}
										</ErrorMessage>
									</div>
								)}
							</div>
						</div>

						<ScrollToTopButton />

						<div className="general-divider"></div>

						<div className="form-button-group">
							<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
								Continue
							</Button>
						</div>

						<GlobalModal isOpen={showAddWitnessModal} setIsOpen={setShowAddWitnessModal} titleText="Add a witness">
							<AddWitnessModal incidentFormData={incidentFormData} setIncidentFormData={setIncidentFormData} />
						</GlobalModal>
					</Form>
				);
			}}
		</Formik>
	);
};

export default StepOne;
